import React, { FC, useState, useEffect, createRef } from 'react';
import {
  Alert,
  Form,
  Input,
  Modal,
  Row,
  Col,
  Select,
  Button,
  Checkbox,
  Card,
  notification,
  InputRef,
  message,
  ModalProps
} from 'antd';
import {
  ArrowRightOutlined,
  PlusOutlined,
  CloseOutlined,
  ArrowLeftOutlined,
  CheckCircleFilled,
  CloseSquareFilled,
  PlusSquareFilled
} from '@ant-design/icons';
import { useAdminApi } from 'api/admin';
import { useRecoilValue } from 'recoil';
import { isAccountFederatedSignonState, profileSwitchState, selectedClientState } from 'recoil/atoms';
import { isString } from 'lodash-es';
import { Auth0User, Role } from 'types/global';
import { ACLCheck } from 'shared/Helpers';
import { selectAssignableUserRoles } from './assignableRoles';

/** Payload the modal hands to `onProvision` in provision mode (domain-agnostic). */
export type AddUserProvisionPayload = {
  firstName: string;
  lastName: string;
  email: string;
  roleIds: number[];
  activate: boolean;
  sendSetPasswordEmail: boolean;
};

/** Result `onProvision` returns so the modal can surface a 409 inline vs a generic error. */
export type AddUserProvisionResult = { ok: true } | { ok: false; alreadyRegistered: boolean; message: string };

interface AddUserModalProps extends ModalProps {
  onSubmit: (values: any) => void;
  roles: Role[];
  initialValues?: any;
  user?: Auth0User;
  availableSeats: number;
  siteAdmin?: boolean;
  emailDomains?: string[];
  isFederatedSignon?: boolean;
  /**
   * Provision mode. When supplied, the modal calls this (instead of the legacy admin/Salesforce
   * create APIs) with the form payload, hides admin-only fields (title, phone), and surfaces a 409
   * inline as "User already registered". The caller performs the create + any side effects and
   * returns the result. Admin mode (no `onProvision`) is unchanged.
   */
  onProvision?: (payload: AddUserProvisionPayload) => Promise<AddUserProvisionResult>;
}

type AddUserFormProps = {
  given_name: string;
  family_name: string;
  email: string;
  email_name: string;
  email_domain: string;
  title: string;
  phone: string;
  active: boolean;
  role_ids: Array<number>;
  selected_role: string | undefined;
};

/**
 * Pragmatic email-format check for the composed `local@domain` value. The domain comes from a
 * validated dropdown, so this mainly guards the user-typed local part (no spaces / `@`, and a
 * well-formed `local@domain.tld` shape).
 */
const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const AddUserModal: FC<AddUserModalProps> = ({
  roles,
  user,
  availableSeats,
  onSubmit,
  emailDomains,
  isFederatedSignon,
  siteAdmin,
  onProvision,
  ...rest
}) => {
  const [form] = Form.useForm<AddUserFormProps>();
  const firstNameRef = createRef<InputRef>();
  const [addedRoles, setAddedRoles] = useState<Role[]>([]);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [blockedButtonDisabled, _setBlockButtonDisabled] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [duplicateError, setDuplicateError] = useState<boolean>(false);
  // Provision mode (deal-picker contact provisioning) vs the legacy admin create/update flow.
  const isProvisionMode = !!onProvision;

  // API
  const {
    updateFederatedUser,
    updateUser,
    updateFederatedClientUser,
    updateClientUser,
    insertUser,
    insertClientUser,
    insertSalesforceUser
  } = useAdminApi();

  const selectedClient = useRecoilValue(selectedClientState);
  const isAccountFederatedSignon = useRecoilValue(isAccountFederatedSignonState);
  const profile = useRecoilValue(profileSwitchState);
  const inputWidth = 320;

  // Check if user has permission to edit email addresses
  const canEditEmail = ACLCheck(['update:all_company_users']);

  // The email local-part input is locked when editing a user without email-edit rights, or for
  // federated/SSO accounts. The stricter format rule must not run against a value the admin cannot
  // change (see the email validator) — this flag is the single source of truth for both.
  const isEmailFieldEditable = !((!!user && !canEditEmail) || isFederatedSignon || isAccountFederatedSignon);

  const handleFinish = async (values: any) => {
    // Provision mode: hand the payload to the caller's new-API create instead of the legacy admin
    // + Salesforce path below. Surfaces a 409 inline; the caller handles success side effects.
    if (onProvision) {
      setSaving(true);
      setDuplicateError(false);
      try {
        const result = await onProvision({
          firstName: values.given_name?.trim(),
          lastName: values.family_name?.trim(),
          email: `${values.email_name?.trim()}@${values.email_domain}`,
          roleIds: addedRoles.map((item) => item.id),
          activate: !!values.active,
          sendSetPasswordEmail: !!values.send_reset_password
        });
        if (result.ok) return;
        if (result.alreadyRegistered) {
          setDuplicateError(true);
          return;
        }
        notification.error({
          message: 'Could not add contact',
          description: result.message,
          duration: 6
        });
      } catch {
        // Any unexpected rejection (network failure, a throw in the caller's success side-effects)
        // must still clear the button loader — the finally below guarantees that.
        notification.error({
          message: 'Could not add contact',
          description: 'Something went wrong while adding the contact. Please try again.',
          duration: 6
        });
      } finally {
        setSaving(false);
      }
      return;
    }

    // setShowError(true);

    /**
     * The identifier of the Salesforce account associated with the selected client.
     * This variable holds the `key` property of the `selectedClient` object.
     * It is typically used to reference and interact with a specific account
     * in Salesforce within the application.
     * If `selectedClient` is undefined or null, `sfAccountId` will be undefined.
     *
     * mhall: changed from the user's client.salesforce_id
     * @type {string | undefined}
     */
    const sfAccountId: string | undefined | number = selectedClient?.key;
    // stop execution if there is no Salesforce account Id, and we are in the
    // Site adminstration module
    if (!isString(sfAccountId) && siteAdmin) return;
    setSaving(true);
    try {
      values.role_ids = addedRoles.map((item) => item.id);
      values.blocked = !values.active;
      values.email = `${values.email_name}@${values.email_domain}`;
      delete values.active;

      let res: any;
      if (user) {
        res =
          siteAdmin && isString(sfAccountId)
            ? isFederatedSignon || isAccountFederatedSignon
              ? await updateFederatedUser(sfAccountId, user?.auth0_user_id, values)
              : await updateUser(sfAccountId, user?.auth0_user_id, values)
            : isFederatedSignon || isAccountFederatedSignon
              ? await updateFederatedClientUser(user?.auth0_user_id, values)
              : await updateClientUser(user?.auth0_user_id, values, profile?.id);
      } else {
        values.provision_salesforce_user = false;
        res =
          siteAdmin && isString(sfAccountId)
            ? await insertUser(sfAccountId, values)
            : await insertClientUser(values, profile?.id);
        if (res?.status == 'success' && res.provision_salesforce_user !== false) {
          const sfRes = await insertSalesforceUserRecord(res);
          // Same two failure modes as the outer guard below. A 200 body-level SF error must be notified
          // here (the interceptor stays silent for it):
          if (sfRes?.status === 'error') {
            notification.error({
              description: 'There was a problem provisioning the user in Salesforce. Please contact support.',
              message: 'Salesforce Provisioning Problem',
              duration: 8
            });
            return;
          }
          // An HTTP failure resolves sfRes to `undefined` and was already notified by the interceptor,
          // so just stop the success flow. Anything that isn't an explicit success also stops here.
          if (!sfRes || sfRes.status !== 'success') {
            return;
          }
        }
      }

      // Two distinct failure modes, handled differently because the useAxios interceptor only notifies
      // on HTTP rejections (network / 4xx / 5xx) — NOT on a 200 response carrying a `{status:'error'}`
      // body (how the controllers report service-layer Auth0/DB failures):
      //   • 200 body-level error → the interceptor stayed silent, so WE must notify here.
      if (res?.status === 'error') {
        notification.error({
          description: 'There was a problem adding/updating a user. Please contact support.',
          message: 'Account Creation/Update Problem',
          duration: 8
        });
        return;
      }
      //   • HTTP failure → res is `undefined` (or a raw axios error) and the interceptor already
      //     notified, so just stop — no second popup, and never fall through to the success path.
      if (!res || res instanceof Error) {
        return;
      }
      onSubmit(values);
    } catch {
      // HTTP failures never reach here — the axios interceptor resolves them (see the guard above).
      // This only fires for an unexpected non-HTTP throw, which the interceptor did NOT notify about,
      // so a single notification here is appropriate (not a duplicate). `finally` clears the loader.
      notification.error({
        description: 'There was a problem adding/updating a user. Please contact support.',
        message: 'Account Creation/Update Problem',
        duration: 8
      });
    } finally {
      setSaving(false);
    }
  };

  const insertSalesforceUserRecord = async (vals: Auth0User) => {
    if (!selectedClient) return;
    let res: any;

    const workingModal = Modal.info({
      title: 'Provisioning Account',
      content: `Provisioning the new account in the NPI Portal.  This may take a few seconds, so thanks in advance for your patience.`,
      closable: false,
      keyboard: false,
      maskClosable: false,
      onCancel: undefined,
      okText: 'Provisioning Account',
      okButtonProps: {
        disabled: true,
        loading: true
      }
    });

    if (typeof selectedClient.key === 'string') {
      res = await insertSalesforceUser({
        Firstname: vals.first_name,
        LastName: vals.last_name,
        Email: vals.email,
        AccountId: selectedClient.key,
        Title: vals.title
      });

      workingModal.destroy();
      // `insertSalesforceUser` resolves to `undefined` on failure (the useAxios interceptor recovers
      // failed requests). Optional-chain the status so a failure can't throw a TypeError here; the
      // failure is surfaced by the interceptor and handled by the caller's not-success guard.
      if (res?.status === 'success') {
        message.success('User account provisioned successfully!');
      }
    }
    return res;
  };

  useEffect(() => {
    if (!rest.open) return;
    setShowError(false);
    setDuplicateError(false);
    form.resetFields();
    if (!user) {
      setAddedRoles([]);

      form.setFieldsValue({
        given_name: '',
        family_name: '',
        email: '',
        email_name: '',
        title: '',
        phone: '',
        // Provision mode defaults both opt-ins off (DB-first); admin mode defaults active by seats.
        active: isProvisionMode ? false : !!availableSeats,
        send_reset_password: false,
        email_domain: Array.isArray(emailDomains) && emailDomains.length === 1 ? emailDomains[0] : undefined,
        role_ids: [],
        selected_role: undefined
      } as AddUserFormProps);
      firstNameRef.current?.focus();
    } else {
      // Roles granted through other APIs that this form does not manage stay hidden and unsubmitted,
      // so the API preserves them instead of reading their absence as a removal.
      const existingRoles: Role[] = selectAssignableUserRoles(user.user_role, roles);
      const roleIds = existingRoles.map(({ id }) => id);
      const [email_name, email_domain] = user.email?.split('@') || [];
      setAddedRoles(existingRoles);

      form.setFieldsValue({
        given_name: user.first_name,
        family_name: user.last_name,
        email: user.email,
        email_name: email_name,
        email_domain: email_domain,
        title: user.title || '',
        phone: user.phone || '',
        active: !user.blocked,
        role_ids: roleIds,
        selected_role: undefined
      } as AddUserFormProps);
      firstNameRef.current?.focus();
    }
    // `roles` is a dependency because the seeded chips are filtered against it: if the picker list
    // arrived after the modal opened, a stale empty list would hide every role the user holds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rest.open, user, form, availableSeats, roles]);

  // useEffect(() => {
  //   if (availableSeats === 0) {
  //     setBlockButtonDisabled(user ? user.blocked === true : true);
  //   } else {
  //     setBlockButtonDisabled(false);
  //   }
  // }, [user, availableSeats]);

  const handleRoleChange = (value: string) => {
    console.log('value ', value);
  };

  const handleAddRole = () => {
    const selectedRole = form.getFieldValue('selected_role');
    const roleToAdd = roles.find((role) => role.id == Number(selectedRole));
    roleToAdd && setAddedRoles([...addedRoles, roleToAdd]);
    form.setFieldValue('selected_role', undefined);
    showError && form.validateFields();
  };

  const handleRemoveRole = (roleId: number) => {
    setAddedRoles(addedRoles.filter((role) => role.id !== roleId));
    form.setFieldValue('selected_role', undefined);
  };

  const handleViewRoles = () => {
    setIsExpanded(true);
    form.setFieldValue('selected_role', undefined);
  };

  const handleHideRoles = () => {
    setIsExpanded(false);
  };

  /**
   * Handles the addition of a role to the current list of added roles.
   * @param {object} role - The role object to be added.
   * @param {number} role.id - The unique identifier of the role.
   * @param {string} role.name - The name of the role.
   * @returns {void} Does not return a value.
   */
  const handleAddRoleClick = (role: { id: number; name: string }): void => {
    setAddedRoles([...addedRoles, role]);
    form.setFieldValue('selected_role', undefined);
  };

  const onBlurInput = (fieldName: string) => {
    let fieldValue = form.getFieldValue(fieldName);
    fieldValue = (fieldValue && fieldValue.trim()) || '';
    form.setFieldValue(fieldName, fieldValue);
  };

  const filteredRoles = roles.filter((role) => !addedRoles.some((addedRole) => addedRole.id === role.id));
  return (
    <Modal
      title={user ? 'Update User Details' : 'Add User Details'}
      open={rest.open}
      width={1200}
      className={'add-user-modal'}
      {...rest}
      footer={[
        <Button key="cancel" onClick={rest.onCancel} disabled={saving}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={saving}
          onClick={() => {
            setShowError(true);
            form.submit();
          }}
          disabled={saving}
        >
          {user ? 'Update user' : 'Add User'}
        </Button>
      ]}
    >
      <Row gutter={16}>
        <Col span={isExpanded ? 12 : 24}>
          <Form
            form={form}
            labelAlign="left"
            size="large"
            layout="horizontal"
            colon={false}
            labelWrap
            onFinish={handleFinish}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 16 }}
          >
            {isProvisionMode && duplicateError && (
              <Alert type="error" showIcon className="mb-4" message="User already registered" />
            )}
            <Form.Item label="First Name" required>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="given_name"
                    rules={[{ required: true, message: 'Please input first name!' }]}
                    required
                  >
                    <Input
                      disabled={isFederatedSignon || isAccountFederatedSignon}
                      placeholder="First name"
                      className="rounded-md"
                      onBlur={() => onBlurInput('given_name')}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="family_name"
                    rules={[{ required: true, message: 'Please input last name!' }]}
                    required
                  >
                    <Input
                      disabled={isFederatedSignon || isAccountFederatedSignon}
                      placeholder="Last name"
                      className="rounded-md"
                      onBlur={() => onBlurInput('family_name')}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>

            {!isProvisionMode && (
              <Form.Item label="Title" name="title">
                <Input
                  disabled={isFederatedSignon || isAccountFederatedSignon}
                  placeholder="Job Title"
                  className="rounded-md"
                  onBlur={() => onBlurInput('title')}
                />
              </Form.Item>
            )}

            <Form.Item
              label="Email"
              name="email_name"
              rules={[
                {
                  validator: () => {
                    if (showError) {
                      const emailName = form.getFieldValue('email_name');
                      const emailDomain = form.getFieldValue('email_domain');
                      if (!emailName) {
                        return Promise.reject(new Error('Please input email!'));
                      } else if (!emailDomain) {
                        return Promise.reject(new Error('Please select email domain!'));
                      }
                      // Only enforce the stricter format on an email the admin can actually edit AND
                      // has changed (or is creating). Never retroactively against a locked/unchanged
                      // stored value — that value may predate this rule and, when the field is
                      // disabled, would permanently block saving unrelated changes (roles, activation).
                      const composedEmail = `${emailName}@${emailDomain}`;
                      const emailChanged = !user || composedEmail !== user.email;
                      if (isEmailFieldEditable && emailChanged && !EMAIL_FORMAT_REGEX.test(composedEmail)) {
                        return Promise.reject(new Error('Invalid email format.'));
                      }
                    }
                    return Promise.resolve();
                  }
                }
              ]}
              required
            >
              <Input
                placeholder="User Name"
                disabled={!isEmailFieldEditable}
                style={{ width: inputWidth }}
                onBlur={() => onBlurInput('email_name')}
                addonAfter={
                  <Form.Item name="email_domain" required noStyle>
                    <Select
                      style={{ width: 190 }}
                      placeholder="Domain"
                      allowClear
                      disabled={!isEmailFieldEditable || (Array.isArray(emailDomains) && emailDomains.length === 1)}
                      onChange={() => {
                        console.log('showError', showError);
                        showError && form.validateFields();
                      }}
                    >
                      {emailDomains?.map((domain: string) => (
                        <Select.Option key={domain} value={domain}>
                          @{domain}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                }
              />
            </Form.Item>

            {!isProvisionMode && (
              <Form.Item
                label="Phone"
                name="phone"
                rules={[
                  {
                    validator: () => {
                      if (showError) {
                        const value: string = form.getFieldValue('phone') || '';
                        const regEx = /^[+]?(\d{1,2})?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
                        if (!value || regEx.test(value)) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(new Error('Please input valid phone number'));
                        }
                      }
                    }
                  }
                ]}
              >
                <Input
                  style={{ width: 240 }}
                  placeholder="+1"
                  disabled={isFederatedSignon || isAccountFederatedSignon}
                  className="rounded-md"
                  onBlur={() => onBlurInput('phone')}
                />
              </Form.Item>
            )}

            <Form.Item label="Select Roles" required>
              <Row gutter={8} align="middle">
                <Col span={isExpanded ? 20 : 12}>
                  <Form.Item
                    name="selected_role"
                    rules={[
                      {
                        validator: () => {
                          if (showError)
                            if (addedRoles.length > 0) {
                              return Promise.resolve(); // No validation error
                            } else {
                              return Promise.reject(new Error('Please select at least one role'));
                            }
                        }
                      }
                    ]}
                  >
                    <Select
                      placeholder="Select roles"
                      className="rounded-md"
                      onChange={handleRoleChange}
                      options={filteredRoles.map((role) => ({ value: role.id, label: role.name }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={isExpanded ? 4 : 12} className={'pb-5'}>
                  <Button
                    icon={
                      <span className="font-bold">
                        <PlusOutlined />
                      </span>
                    }
                    onClick={handleAddRole}
                  />
                  {!isExpanded ? (
                    <span className="ml-2 text-blue-600 cursor-pointer" onClick={handleViewRoles}>
                      View available roles <ArrowRightOutlined />
                    </span>
                  ) : (
                    <></>
                  )}
                </Col>
              </Row>
              {addedRoles.length > 0 ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  {addedRoles.map((role) => (
                    <div key={role.id} className="flex items-center px-3 py-1 rounded-full bg-gray-300 text-xs">
                      {role.name}
                      <Button
                        type="text"
                        icon={<CloseOutlined style={{ fontSize: '8px', color: 'gray', fontWeight: '600' }} />}
                        className="ml-2"
                        style={{
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          padding: '0',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                        onClick={() => handleRemoveRole(role.id)}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <></>
              )}
            </Form.Item>
            <Form.Item>
              <Row>
                <Col span={9}></Col>
                <Col span={12}>
                  <Form.Item name="active" valuePropName="checked">
                    <Checkbox disabled={blockedButtonDisabled}>Activate User</Checkbox>
                  </Form.Item>
                </Col>
              </Row>
              {!user && (
                <Row>
                  <Col span={9}></Col>
                  <Col span={12}>
                    <Form.Item label="" name="send_reset_password" valuePropName="checked">
                      <Checkbox>
                        {`Send "Set Password" Email`}
                        {isProvisionMode && (
                          <span className="ml-2 text-xs text-gray-400">(applies to non-SSO users only)</span>
                        )}
                      </Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Form.Item>
          </Form>
        </Col>
        {isExpanded ? (
          <Col span={12}>
            <div
              className="p-4 border-l"
              style={{
                backgroundColor: '#f0f0f0',
                maxHeight: '400px',
                overflowY: 'auto',
                msOverflowStyle: 'none'
              }}
            >
              <Button onClick={handleHideRoles} type="link" className="text-base">
                <ArrowLeftOutlined /> Hide Roles
              </Button>
              {roles.map((role) => {
                const isRoleAdded = addedRoles.find((addedRole) => addedRole.id == role.id);
                return (
                  <Card
                    key={role.id}
                    className={`mb-2 border ${isRoleAdded ? 'border-green-500' : ''}`}
                    style={{
                      borderRadius: '8px',
                      padding: '10px 0',
                      transition: 'border-color 0.3s',
                      cursor: 'pointer',
                      borderColor: isRoleAdded ? 'green' : 'transparent'
                    }}
                    onMouseOver={(e) => {
                      if (!isRoleAdded) {
                        e.currentTarget.style.borderColor = 'blue';
                        const addRoleText = e.currentTarget.querySelector('.add-role-text') as HTMLElement;
                        if (addRoleText) {
                          addRoleText.style.display = 'flex';
                        }
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isRoleAdded) {
                        e.currentTarget.style.borderColor = 'transparent';
                        const addRoleText = e.currentTarget.querySelector('.add-role-text') as HTMLElement;
                        if (addRoleText) {
                          addRoleText.style.display = 'none';
                        }
                      }
                    }}
                  >
                    <div>
                      <div className="flex items-center">
                        <span className={'w-5'}>
                          <CheckCircleFilled
                            className={'text-green-500 mr-2'}
                            style={!isRoleAdded ? { display: 'none' } : {}}
                          />
                        </span>
                        <span className="font-bold">{role.name}</span>
                        {isRoleAdded ? (
                          <span
                            className="text-red-600 ml-auto flex items-center cursor-pointer font-bold"
                            onClick={() => handleRemoveRole(role.id)}
                          >
                            <CloseSquareFilled className="mr-1" />
                            Remove
                          </span>
                        ) : (
                          <span
                            className="text-blue-600 ml-auto add-role-text flex items-center cursor-pointer font-bold"
                            style={{ display: 'none' }}
                            onClick={() => handleAddRoleClick(role)}
                          >
                            <PlusSquareFilled className="mr-1" />
                            Click to add role
                          </span>
                        )}
                      </div>
                      <div className={'pl-5'}>{role.description || ''}</div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </Col>
        ) : (
          <></>
        )}
      </Row>
    </Modal>
  );
};
